
import React, { useCallback, useEffect, useState } from "react";
import {
    ArrowUpRight,
    Globe,
    MapPin,
    RefreshCw,
    Users,
    TrendingUp,
    Award,
    Handshake,
    BookOpen,
    Megaphone,
} from "lucide-react";


import "./PartnerNetwork.css";

const PARTNERS_API = "/api/partners/all";

const getWebsite = (website) => {
    if (!website?.trim()) return null;

    try {
        const normalized = /^https?:\/\//i.test(website.trim())
            ? website.trim()
            : `https://${website.trim()}`;

        const url = new URL(normalized);

        return ["http:", "https:"].includes(url.protocol)
            ? url.href
            : null;
    } catch {
        return null;
    }
};

const getAddress = (partner) => {
    const address = partner.address?.trim();

    const locationParts = [
        partner.city,
        partner.state,
        partner.pincode,
    ]
        .filter(Boolean)
        .map((value) => String(value).trim());

    const uniqueParts = [...new Set(locationParts)];

    return [
        address,
        ...uniqueParts.filter(
            (part) =>
                !address?.toLowerCase().includes(part.toLowerCase())
        ),
    ]
        .filter(Boolean)
        .join(", ");
};

function PartnerNetwork() {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchPartners = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await fetch(PARTNERS_API);

            if (!response.ok) {
                throw new Error("Unable to load partners.");
            }

            const data = await response.json();

            if (!Array.isArray(data)) {
                throw new Error("Invalid partner data received.");
            }

            const approvedPartners = data.filter(
                (partner) =>
                    String(partner.status || "")
                        .trim()
                        .toUpperCase() === "APPROVED"
            );

            setPartners(approvedPartners);
        } catch (err) {
            setError(
                err.message || "Something went wrong while loading partners."
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPartners();
    }, [fetchPartners]);

    return (
        <div className="partner-network-page">
            <header className="pn-hero">
                <div className="pn-hero-grid" />

                <div className="pn-hero-inner">
                    <span className="pn-eyebrow">
                        The CertED Community
                    </span>

                    <h1>
                        Our Partner <span>Network</span>
                    </h1>

                    <p>
                        Discover the organizations partnering with CertED
                        to connect skills, knowledge, and professional
                        training opportunities.
                    </p>

                    <div className="pn-hero-bar" />
                </div>
            </header>

            <main className="pn-section">
                <section className="pn-panel">
                    <div className="pn-head">
                        <div>
                            <div className="pn-kicker">Our Community</div>
                            <h2>Meet Our Partners</h2>
                            <p>
                                Organizations helping build a stronger training
                                ecosystem.
                            </p>
                        </div>

                        <span className="pn-count">
                            <span className="pn-count-dot" />
                            {partners.length}{" "}
                            {partners.length === 1 ? "Partner" : "Partners"}
                        </span>
                    </div>

                    {loading && (
                        <div className="pn-message">
                            Loading our partner network...
                        </div>
                    )}

                    {!loading && error && (
                        <div className="pn-message pn-error">
                            <p>{error}</p>

                            <button
                                type="button"
                                className="pn-retry"
                                onClick={fetchPartners}
                            >
                                <RefreshCw size={14} />
                                Try Again
                            </button>
                        </div>
                    )}

                    {!loading && !error && partners.length === 0 && (
                        <div className="pn-message">
                            No approved partners are available right now.
                        </div>
                    )}

                    {!loading && !error && partners.length > 0 && (
                        <div className="pn-grid">
                            {partners.map((partner) => {
                                const name =
                                    partner.companyName?.trim() ||
                                    "Training Partner";

                                const address = getAddress(partner);

                                const website = getWebsite(partner.website);

                                return (
                                    <article
                                        className="pn-card"
                                        key={partner.id}
                                    >
                                        <h3>{name}</h3>

                                        <span className="pn-type">
                                            {partner.companyType || "Training Partner"}
                                        </span>

                                        {address && (
                                            <div className="pn-detail">
                                                <MapPin aria-hidden="true" size={16} />
                                                <p>{address}</p>
                                            </div>
                                        )}

                                        <div className="pn-card-footer">
                                            {website ? (
                                                <a
                                                    className="pn-website"
                                                    href={website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Globe aria-hidden="true" />
                                                    Visit Website
                                                    <ArrowUpRight aria-hidden="true" />
                                                </a>
                                            ) : (
                                                <span className="pn-no-website">
                                                    Website not available
                                                </span>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </section>
<div className="pn-main">
                <section className="pn-benefits">
                    <div className="pn-benefits-heading">
                        <span className="pn-kicker">Why Partner With CertED?</span>

                        <h2>
                            How Our Partners <span>Benefit</span>
                        </h2>

                        <p>
                            Grow your reach, showcase your expertise, and connect
                            with a community focused on skills and professional
                            development.
                        </p>
                    </div>

                    <div className="pn-benefits-grid">
                        <article className="pn-benefit-card">
                            <div className="pn-benefit-icon">
                                <Megaphone size={25} />
                            </div>

                            <h3>Greater Visibility</h3>

                            <p>
                                Showcase your organization to learners, trainers,
                                and other professionals through the CertED partner network.
                            </p>
                        </article>

                        <article className="pn-benefit-card">
                            <div className="pn-benefit-icon">
                                <Users size={25} />
                            </div>

                            <h3>Connect With Learners</h3>

                            <p>
                                Build connections with people seeking training,
                                skill development, and new learning opportunities.
                            </p>
                        </article>

                        <article className="pn-benefit-card">
                            <div className="pn-benefit-icon">
                                <TrendingUp size={25} />
                            </div>

                            <h3>Growth Opportunities</h3>

                            <p>
                                Expand your professional network and explore
                                opportunities to collaborate with other organizations.
                            </p>
                        </article>

                        <article className="pn-benefit-card">
                            <div className="pn-benefit-icon">
                                <Award size={25} />
                            </div>

                            <h3>Showcase Your Expertise</h3>

                            <p>
                                Highlight your training services, organizational
                                strengths, and areas of specialization.
                            </p>
                        </article>

                        <article className="pn-benefit-card">
                            <div className="pn-benefit-icon">
                                <Handshake size={25} />
                            </div>

                            <h3>Build Partnerships</h3>

                            <p>
                                Discover potential collaborations with training
                                providers and organizations in the CertED community.
                            </p>
                        </article>

                        <article className="pn-benefit-card">
                            <div className="pn-benefit-icon">
                                <BookOpen size={25} />
                            </div>

                            <h3>Support Skill Development</h3>

                            <p>
                                Contribute to a stronger learning ecosystem by
                                connecting people with relevant training opportunities.
                            </p>
                        </article>
                    </div>
                </section>
                </div>

            </main>
        </div>
    );
}

export default PartnerNetwork;
